import { CardBottomSection } from '@dealstack/frontend/shared/components/ActionCard/Components/CardBottomSection';
import { CardButton } from '@dealstack/frontend/shared/components/ActionCard/Components/CardButton';
import { CardHeader } from '@dealstack/frontend/shared/components/ActionCard/Components/CardHeader';
import { TagPair } from '@dealstack/frontend/shared/components/ActionCard/Components/TagPair';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useGetComponentsFromChildren } from '@dealstack/shared/lib/useGetComponentsFromChildren';
import { rem } from '@mantine/core';
import { PropsWithChildren } from 'react';
import styled from 'styled-components';

interface Props {
	className?: string;
	title?: string;
	subtitle?: string | null;
	secondTitle?: string;
	secondSubtitle?: string | React.ReactNode;
	tooltipText?: string | React.ReactNode;
}

const ActionCardComponent = ({
	className,
	children,
	title,
	subtitle,
	secondTitle,
	secondSubtitle,
	tooltipText,
}: PropsWithChildren<Props>) => {
	const [cardHeader, tagPair, cardButton, cardBottomSection] = useGetComponentsFromChildren(
		children,
		[
			ActionCard.CardHeader,
			ActionCard.TagPair,
			ActionCard.CardButton,
			ActionCard.CardBottomSection,
		]
	);

	return (
		<div className={className}>
			{cardHeader}
			{tagPair && <TagPair title={title} subtitle={subtitle} />}
			{tagPair && (
				<TagPair title={secondTitle} subtitle={secondSubtitle} tooltipText={tooltipText} />
			)}
			<CardFooter>
				{cardButton}
				{cardBottomSection}
			</CardFooter>
		</div>
	);
};

const CardFooter = styled.div`
	margin-top: auto;
	display: flex;
	flex-direction: column;
	gap: ${rem(4)};
`;

const Styled = styled(ActionCardComponent)`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: ${rem(8)};
	border-radius: 0.5rem;
	border-bottom: 1px solid ${defaultTheme.v3.colors.dark[4]};
	min-width: ${rem(320)};
	min-height: ${rem(232)};
	background-color: ${defaultTheme.v3.colors.dark[6]};
	${CardBottomSection} {
		min-height: ${rem(40)};
	}
`;

type ActionCardType = typeof ActionCardComponent & {
	CardHeader: typeof CardHeader;
	TagPair: typeof TagPair;
	CardButton: typeof CardButton;
	CardBottomSection: typeof CardBottomSection;
};
export const ActionCard: ActionCardType = Styled as unknown as ActionCardType;
ActionCard.CardHeader = CardHeader;
ActionCard.TagPair = TagPair;
ActionCard.CardButton = CardButton;
ActionCard.CardBottomSection = CardBottomSection;
